import { getAllPatients } from '../constants/mockPatients';
import { normalizeRoom, resolvePatientMatch } from './patientMatch';

function normalizeName(name) {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .trim()
    .replace(/[.'’]/g, '')
    .replace(/\s+/g, ' ');
}

function nameTokens(name) {
  return normalizeName(name)
    .split(' ')
    .filter((token) => token.length >= 2);
}

function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseRoomToken(token) {
  if (!token) return null;
  const raw = String(token).trim().toLowerCase();
  if (!raw) return null;
  return normalizeRoom(raw);
}

/** Strict first-name fuzzy: Leyla≈Layla (1 edit), not Omar≈over (2 edits). */
function firstNamesClose(a, b) {
  const left = normalizeName(a);
  const right = normalizeName(b);
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.length < 4 || right.length < 4) return false;
  if (Math.abs(left.length - right.length) > 1) return false;
  if (left[0] !== right[0]) return false;

  // Inline Levenshtein — allow at most one substitution/insert/delete.
  const rows = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let i = 1; i <= left.length; i += 1) {
    let previous = rows[0];
    rows[0] = i;
    for (let j = 1; j <= right.length; j += 1) {
      const temp = rows[j];
      rows[j] =
        left[i - 1] === right[j - 1]
          ? previous
          : 1 + Math.min(previous, rows[j], rows[j - 1]);
      previous = temp;
    }
  }
  return rows[right.length] <= 1;
}

const FUZZY_NAME_STOPWORDS = new Set([
  'over',
  'under',
  'blood',
  'pressure',
  'pulse',
  'pain',
  'room',
  'bed',
  'patient',
  'stable',
  'fever',
  'given',
  'gave',
  'dose',
  'oral',
  'plan',
  'wound',
  'clean',
  'temp',
  'temperature',
]);

function sentenceStartBefore(text, index) {
  let start = 0;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (/[.!?\n]/.test(text[i])) {
      start = i + 1;
      while (start < index && /\s/.test(text[start])) start += 1;
      break;
    }
  }
  return start;
}

function findRoomAnchors(transcript) {
  const anchors = [];
  const pattern = /\b(?:room|bed|in\s+room|غرفة|رقم\s+الغرفة)\s*#?\s*(\d+[a-z]?)\b/gi;
  let match = pattern.exec(transcript);
  while (match) {
    const room = parseRoomToken(match[1]);
    if (!room) {
      match = pattern.exec(transcript);
      continue;
    }
    anchors.push({
      // "Panadol given to patient in Room 3" → segment starts at Panadol, not Room
      index: sentenceStartBefore(transcript, match.index),
      room,
      label: match[0],
    });
    match = pattern.exec(transcript);
  }
  return anchors;
}

function findRosterRoomAnchors(transcript, patients) {
  const anchors = [];
  const rooms = [...new Set((patients || []).map((patient) => normalizeRoom(patient.room)).filter(Boolean))];

  rooms.forEach((room) => {
    const pattern = new RegExp(`\\b(?:room|bed|in\\s+room|غرفة|رقم\\s+الغرفة)\\s*#?\\s*${escapeRegex(room)}\\b`, 'gi');
    let match = pattern.exec(transcript);
    while (match) {
      anchors.push({
        index: sentenceStartBefore(transcript, match.index),
        room,
        label: match[0],
      });
      match = pattern.exec(transcript);
    }
  });

  return anchors;
}

function findTransitionAnchors(transcript) {
  const anchors = [];
  const patterns = [
    /\b(?:and\s+)?room\s*mate(?:'s)?\s+(?:vitals|bp|blood\s+pressure|meds|medications|notes?)?\b/gi,
    /\b(?:and\s+)?roommate(?:'s)?\s+(?:vitals|bp|blood\s+pressure|meds|medications|notes?)?\b/gi,
    /\bnext\s+patient\b/gi,
    /\bsecond\s+patient\b/gi,
    /\b(?:and\s+)?for\s+(?:the\s+)?(?:other|second)\s+patient\b/gi,
    /\bالمريض\s+التاني\b/gi,
    /\bمريض\s+ثاني\b/gi,
  ];

  patterns.forEach((pattern) => {
    let match = pattern.exec(transcript);
    while (match) {
      anchors.push({
        index: match.index,
        room: null,
        label: match[0],
        transition: true,
      });
      match = pattern.exec(transcript);
    }
  });

  return anchors;
}

/**
 * "Patient Ahmad Room 3" / "مريضة سارة غرفة 7" style section headers.
 * Also "Patient Leyla" / "Patient Omar" without room, and "Ahmad — …" separators.
 */
function findPatientHeaderAnchors(transcript) {
  const anchors = [];
  const patterns = [
    /\bpatient\s+([\p{L}][\p{L}'.-]{1,40}(?:\s+[\p{L}][\p{L}'.-]{1,40}){0,3})\s+(?:room|bed)\s*#?\s*(\d+[a-z]?)\b/giu,
    /(?:^|[.!?\n]\s*)مريض(?:ة)?\s+([\p{L}][\p{L}'.-]{1,40}(?:\s+[\p{L}][\p{L}'.-]{1,40}){0,3})\s+غرفة\s*#?\s*(\d+[a-z]?)/giu,
    /(?:^|[.!?\n]\s*)(?:patient|مريض(?:ة)?)\s*[:：]\s*([\p{L}][\p{L}\s'.-]{1,60}?)(?:\s+(?:room|bed|غرفة)\s*#?\s*(\d+[a-z]?))?/giu,
    // Cleanup form: "Patient 1: Ahmed." / "Patient 2: Omar."
    /\bpatient\s*#?\s*[1-9]\d*\s*[:.\-–—]\s*([\p{L}][\p{L}'.-]{1,40}(?:\s+[\p{L}][\p{L}'.-]{1,40}){0,3})/giu,
    // "Patient Leyla." / "Patient Omar blood pressure…" — name without room/colon
    /\bpatient\s+([\p{L}][\p{L}'.-]{2,40})(?=\s*[.:,]|\s+(?:blood|bp|pulse|temp|pain|fever|stable|room|bed|wound|post|gave|given|on|has|had)\b)/giu,
  ];

  patterns.forEach((pattern) => {
    let match = pattern.exec(transcript);
    while (match) {
      const spokenName = String(match[1] || '').trim();
      const room = parseRoomToken(match[2]);
      // Skip filler after "patient" ("patient is stable")
      if (/^(is|was|has|will|should|can|with|for|in|the|a|an)$/i.test(spokenName)) {
        match = pattern.exec(transcript);
        continue;
      }
      anchors.push({
        index: match.index,
        room: room || null,
        spokenName: spokenName || null,
        label: match[0],
        transition: true,
      });
      match = pattern.exec(transcript);
    }
  });

  // "Ahmad — paracetamol…" / "Sara — fever…" without "Patient" prefix
  const nameDashPattern =
    /(?:^|[.!?\n]\s*)([\p{L}][\p{L}'.-]{2,40}(?:\s+[\p{L}][\p{L}'.-]{1,40}){0,2})\s*[—–\-]\s+/giu;
  const nameDashMatches = [];
  let nameDashMatch = nameDashPattern.exec(transcript);
  while (nameDashMatch) {
    nameDashMatches.push(nameDashMatch);
    nameDashMatch = nameDashPattern.exec(transcript);
  }
  if (nameDashMatches.length >= 2) {
    nameDashMatches.forEach((match) => {
      const spokenName = String(match[1] || '').trim();
      if (!spokenName) return;
      const nameOffset = match[0].search(/[\p{L}]/u);
      anchors.push({
        index: match.index + (nameOffset >= 0 ? nameOffset : 0),
        room: null,
        spokenName,
        label: match[0],
        transition: true,
      });
    });
  }

  return anchors;
}

function findNameAnchors(transcript, patients) {
  const anchors = [];
  const text = String(transcript || '');
  const words = text.match(/[\p{L}][\p{L}'.-]{2,40}/gu) || [];

  for (const patient of patients || []) {
    for (const name of [patient.nameEn, patient.name].filter(Boolean)) {
      const normalized = normalizeName(name);
      if (!normalized) continue;

      const fullPattern = new RegExp(
        `(?<![\\p{L}])${escapeRegex(normalized).replace(/\s+/g, '\\s+')}(?![\\p{L}])`,
        'iu'
      );
      const fullMatch = fullPattern.exec(text);
      if (fullMatch) {
        anchors.push({
          index: fullMatch.index,
          room: normalizeRoom(patient.room),
          patientId: patient.id,
          label: fullMatch[0],
        });
        continue;
      }

      const firstToken = nameTokens(name)[0];
      if (!firstToken) continue;
      const isArabic = /[\u0600-\u06FF]/.test(firstToken);
      const minLen = isArabic ? 2 : 3;
      if (firstToken.length < minLen) continue;

      // Exact first-name hits
      const tokenPattern = new RegExp(
        `(?<![\\p{L}])${escapeRegex(firstToken)}(?![\\p{L}])`,
        'giu'
      );
      let tokenMatch = tokenPattern.exec(text);
      let matched = false;
      while (tokenMatch) {
        matched = true;
        anchors.push({
          index: tokenMatch.index,
          room: normalizeRoom(patient.room),
          patientId: patient.id,
          label: tokenMatch[0],
        });
        tokenMatch = tokenPattern.exec(text);
      }
      if (matched) continue;

      // Fuzzy first-name (Leyla ≈ Layla) against spoken words — never clinical stopwords
      for (const word of words) {
        const normalizedWord = normalizeName(word);
        if (FUZZY_NAME_STOPWORDS.has(normalizedWord)) continue;
        if (!firstNamesClose(firstToken, word)) continue;
        const idx = text.toLowerCase().indexOf(word.toLowerCase());
        if (idx < 0) continue;
        anchors.push({
          index: idx,
          room: normalizeRoom(patient.room),
          patientId: patient.id,
          label: word,
          spokenName: word,
        });
        break;
      }
    }
  }
  return anchors;
}

function dedupeAnchors(anchors) {
  const sorted = [...anchors].sort((a, b) => a.index - b.index);
  const deduped = [];

  sorted.forEach((anchor) => {
    const last = deduped[deduped.length - 1];
    if (!last) {
      deduped.push({ ...anchor });
      return;
    }

    if (anchor.index - last.index < 24) {
      if (!last.patientId && anchor.patientId) last.patientId = anchor.patientId;
      if (!last.room && anchor.room) last.room = anchor.room;
      if (!last.spokenName && anchor.spokenName) last.spokenName = anchor.spokenName;
      if (anchor.transition) last.transition = true;
      return;
    }

    deduped.push({ ...anchor });
  });

  return deduped;
}

function ensureLeadingAnchor(anchors) {
  if (!anchors.length) return anchors;
  const sorted = [...anchors].sort((a, b) => a.index - b.index);
  const hasTransition = sorted.some((anchor) => anchor.transition);
  if (hasTransition && sorted[0].index > 0) {
    return dedupeAnchors([
      { index: 0, room: null, label: 'segment_start', implicit: true },
      ...sorted,
    ]);
  }
  return sorted;
}

function segmentIdentity(segment) {
  if (segment.patientId) return `patient:${segment.patientId}`;
  if (segment.roomHint) return `room:${segment.roomHint}`;
  if (segment.patientHints?.name) {
    return `name:${String(segment.patientHints.name).toLowerCase().slice(0, 32)}`;
  }
  return `text:${segment.transcript.slice(0, 32).toLowerCase()}`;
}

/**
 * Same patient mentioned twice (e.g. Room 3 vitals later Room 3 meds)
 * must stay one segment so note-type detection sees the full content.
 */
function mergeSegmentsByPatient(segments) {
  const merged = [];

  const findTargetIndex = (segment) => {
    if (segment.patientId) {
      const byPatient = merged.findIndex((item) => item.patientId === segment.patientId);
      if (byPatient >= 0) return byPatient;
    }
    const room = normalizeRoom(segment.roomHint || segment.patient?.room);
    if (room) {
      const byRoom = merged.findIndex((item) => {
        const existingRoom = normalizeRoom(item.roomHint || item.patient?.room);
        return existingRoom && existingRoom === room;
      });
      if (byRoom >= 0) return byRoom;
    }
    return -1;
  };

  segments.forEach((segment) => {
    const targetIndex = findTargetIndex(segment);
    if (targetIndex < 0) {
      merged.push({
        ...segment,
        patientHints: { ...(segment.patientHints || {}) },
      });
      return;
    }

    const existing = merged[targetIndex];
    existing.transcript = `${existing.transcript}\n${segment.transcript}`.trim();
    if (!existing.patientId && segment.patientId) {
      existing.patientId = segment.patientId;
      existing.patient = segment.patient;
      existing.match = segment.match;
    }
    if (!existing.roomHint && segment.roomHint) {
      existing.roomHint = segment.roomHint;
    }
    existing.patientHints = {
      room: existing.patientHints?.room || segment.patientHints?.room || null,
      name: existing.patientHints?.name || segment.patientHints?.name || null,
    };
  });

  return merged;
}

function countDistinctRooms(transcript) {
  const rooms = new Set();
  const pattern = /\b(?:room|bed|غرفة)\s*#?\s*(\d+[a-z]?)\b/gi;
  let match = pattern.exec(transcript);
  while (match) {
    const room = parseRoomToken(match[1]);
    if (room) rooms.add(room);
    match = pattern.exec(transcript);
  }
  return rooms.size;
}

function countPatientRoomHeaders(transcript) {
  const text = String(transcript || '');
  const en =
    text.match(
      /\bpatient\s+[\p{L}][\p{L}'.\s-]{0,60}?\s+(?:room|bed)\s*#?\s*\d+[a-z]?\b/giu
    ) || [];
  const ar =
    text.match(/مريض(?:ة)?\s+[\p{L}][\p{L}'.\s-]{0,60}?\s+غرفة\s*\d+[a-z]?\b/giu) || [];
  return en.length + ar.length;
}

/** "Patient 1:" / "Patient 2:" / "مريض 1" numbered section headers from cleanup. */
function countNumberedPatientHeaders(transcript) {
  const text = String(transcript || '');
  const en = text.match(/\bpatient\s*#?\s*[1-9]\d*\s*[:.\-–—]/gi) || [];
  const ar = text.match(/(?:^|[.!?\n]\s*)(?:المريض|مريض(?:ة)?)\s*#?\s*[1-9]\d*\s*[:.\-–—]/gim) || [];
  const ordinal =
    text.match(
      /(?:^|[.!?\n]\s*)(?:first|second|third|fourth|أول|ثاني|ثالث|رابع)\s+patient/gi
    ) || [];
  const ordinalAr =
    text.match(/(?:^|[.!?\n]\s*)(?:أول|ثاني|ثالث|رابع)\s*مريض/gi) || [];
  return en.length + ar.length + ordinal.length + ordinalAr.length;
}

/**
 * Shift handover recordings mention multiple patients in one narrative.
 * They must be generated as a single handover note (patients[] array), not split
 * into per-segment vitals/MAR-style generation.
 *
 * Multi-patient alone is NOT enough — "Patient Ahmad Room 3… Patient Sara Room 7…"
 * without handover framing should split so each segment gets its own note type.
 */
export function isUnifiedHandoverRecording(transcript) {
  const text = String(transcript || '').trim();
  if (!text) return false;

  const lower = text.toLowerCase();

  const shiftHandoverMarkers = [
    'shift handover',
    'shift report',
    'handover report',
    'sbar handover',
    'تسليم الشيفت',
    'تسليم شيفت',
    'تسليم',
  ];
  if (shiftHandoverMarkers.some((marker) => lower.includes(marker))) {
    return true;
  }

  const hasPatientEnumeration =
    /(?:^|[.!?\n]\s*)(?:أول|ثاني|ثالث|رابع)\s*مريض|(?:first|second|third)\s+patient/i.test(text);

  const roomMentions = text.match(/\b(?:room|bed|غرفة)\s*#?\s*\d+[a-z]?\b/gi) || [];
  const distinctRooms = countDistinctRooms(text);
  const multiPatientHeaders =
    countPatientRoomHeaders(text) >= 2 || countNumberedPatientHeaders(text) >= 2;

  // Explicit next-shift / follow-up language with multiple patients or rooms
  const handoverish =
    /الشيفت|الوردية|handover|next\s+shift|يراجع|متابعة|outstanding|follow[\s-]?up|incoming\s+nurse|next\s+nurse/i.test(
      text
    );

  if (hasPatientEnumeration && (roomMentions.length >= 2 || /\bhandover\b/i.test(lower))) {
    return true;
  }

  if (distinctRooms >= 2 && handoverish) {
    return true;
  }

  // Multiple patient headers only stay unified when handover framing is also present
  if (multiPatientHeaders && handoverish) {
    return true;
  }

  return false;
}

export function splitTranscriptByPatients(transcript, patients = []) {
  const text = String(transcript || '').trim();
  // Room/header splits do not require a full roster; name anchors do when available.
  if (!text) return [];

  const anchors = ensureLeadingAnchor(
    dedupeAnchors([
      ...findRoomAnchors(text),
      ...findRosterRoomAnchors(text, patients),
      ...findNameAnchors(text, patients),
      ...findPatientHeaderAnchors(text),
      ...findTransitionAnchors(text),
    ])
  );

  if (anchors.length < 2) return [];

  const segments = anchors.map((anchor, index) => {
    const start = anchor.index;
    const end = index + 1 < anchors.length ? anchors[index + 1].index : text.length;
    const segmentText = text.slice(start, end).trim();
    const spokenName = anchor.spokenName || null;
    const patientHints = {
      room: anchor.room || null,
      name: spokenName || anchor.label || null,
    };

    // Prefer spoken header name over room-only roster match when they disagree.
    let patientId = anchor.patientId || null;
    let match = resolvePatientMatch({
      patients,
      transcript: segmentText,
      patientHints,
      patientId,
    });

    if (
      spokenName &&
      match.patient &&
      !namesLikelySame(spokenName, match.patient.nameEn) &&
      !namesLikelySame(spokenName, match.patient.name)
    ) {
      patientId = null;
      match = {
        status: 'unresolved',
        patient: null,
        candidates: match.candidates || [],
      };
    }

    return {
      transcript: segmentText,
      roomHint: anchor.room || null,
      patientId: patientId || match.patient?.id || null,
      patient: match.patient || null,
      match,
      patientHints,
      isTransition: Boolean(anchor.transition),
    };
  }).filter((segment) => segment.transcript.length > 0);

  if (segments.length < 2) return [];

  const merged = mergeSegmentsByPatient(segments);
  if (merged.length < 2) return [];

  const identities = new Set(merged.map(segmentIdentity));
  if (identities.size < 2) return [];

  return merged;
}

function namesLikelySame(a, b) {
  const left = String(a || '')
    .toLowerCase()
    .replace(/[.'’]/g, '')
    .trim();
  const right = String(b || '')
    .toLowerCase()
    .replace(/[.'’]/g, '')
    .trim();
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.includes(right) || right.includes(left)) return true;
  if (firstNamesClose(left, right)) return true;
  const leftFirst = left.split(/\s+/)[0];
  const rightFirst = right.split(/\s+/)[0];
  if (leftFirst.length >= 3 && leftFirst === rightFirst) return true;
  return firstNamesClose(leftFirst, rightFirst);
}

export function isMultiPatientTranscript(transcript, admittedPatients = []) {
  return splitTranscriptByPatients(transcript, getAllPatients(admittedPatients)).length >= 2;
}
